import React, { FC, useState } from 'react';

import {
  Box,
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikMultiSelect from 'components/primitives/FormikMultiSelect';
import PlacesAutocomplete from 'components/primitives/PlacesAutocomplete';

import { days, pickupFrequency } from 'constants/weekdays';
import composeFullAddress from 'utils/composeFullAddress';

import {
  getInitialValues,
  validationSchema,
} from './AddCollectionPointModal.utils';

import { EditCollectionPointViewProps } from './AddCollectionPointModal.props';
import { parseGoogleGeocoderResultIntoAddressComponents } from '../AddCollectionPoint/AddCollectionPointModal.utils';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const EditCollectionPointModalView: FC<EditCollectionPointViewProps> = (
  props,
) => {
  const {
    point,
    sites,
    isOpen,
    onClose,
    onUpdate,
    isUpdating,
    isLoadingSites,
  } = props;

  const [showZipInput, setShowZipInput] = useState(false);

  const handleOnSelectPlacesAutocomplete = (
    result: google.maps.GeocoderResult[],
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined,
    ) => void,
  ) => {
    setShowZipInput(false);
    const addressComponents = parseGoogleGeocoderResultIntoAddressComponents(
      result,
    );

    setFieldValue('address', addressComponents);

    const shouldToCheckForShowZipInput =
      result.length > 0 && !Boolean(addressComponents.zip);
    if (shouldToCheckForShowZipInput) {
      setShowZipInput(true);
    }
  };

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader color="support.500" textAlign="center">
          Update collection point - {point?._id || ''}
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" p={6}>
            <Formik
              initialValues={getInitialValues(point)}
              validationSchema={validationSchema}
              onSubmit={onUpdate}>
              {({ values, isValid, setFieldValue }) => (
                <Form>
                  <Flex>
                    <FormikInput
                      name="name"
                      label="Name (*)"
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                      placeholder="E.g. 'John's Restaurant'"
                    />
                    <Box h="20px" w="20px" />
                    <FormikSelectInput
                      placeholder="Select Type"
                      label="Type"
                      isLoading={isLoadingSites}
                      containerProps={{ flex: 1 }}
                      name="type">
                      <option value="site">Site</option>
                      <option value="city-wide">City-wide</option>
                      <option value="campus">Campus</option>
                    </FormikSelectInput>
                    <Box h="20px" w="20px" />
                    <FormikSelectInput
                      placeholder="Select Site"
                      label="Linked Site"
                      isLoading={isLoadingSites}
                      containerProps={{ flex: 1 }}
                      name="site">
                      {sites?.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s?.name || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                  </Flex>
                  <Flex mt={6}>
                    <FormikSelectInput
                      placeholder="Select Pickup Frequency"
                      label="Pickup Frequency (*)"
                      name="pickupFrequency"
                      containerProps={{ flex: 1 }}>
                      {pickupFrequency.map((w) => (
                        <option key={w} value={w}>
                          {w || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                    <Box h="20px" w="20px" />
                    {['Weekly', 'Bi-Weekly', 'Monthly'].includes(
                      values?.pickupFrequency,
                    ) && (
                      <FormikMultiSelect
                        options={days}
                        containerProps={{ flex: 1 }}
                        placeholder="Select"
                        label="Pickup Day Of The Week"
                        name="pickupDayOfTheWeek"
                      />
                    )}
                  </Flex>
                  <Flex mt={6}>
                    <PlacesAutocomplete
                      name="address"
                      labelText="Address (*)"
                      labelProps={labelProps}
                      onSelect={(result) =>
                        handleOnSelectPlacesAutocomplete(result, setFieldValue)
                      }
                      // addressLine1 is only the street, so show the whole
                      // address, as the site and wash hub forms do.
                      defaultInputValue={composeFullAddress(values?.address)}
                    />
                    {showZipInput && (
                      <>
                        <Box h="20px" w="20px" />
                        <FormikInput
                          name="address.zip"
                          label="Zip code (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1 }}
                          placeholder="your text here"
                          onInput={(value) => {
                            setFieldValue('address.zip', value);
                            console.log(value);
                          }}
                        />
                      </>
                    )}
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="deliveryInstructions"
                      label="Pickup Instructions"
                      placeholder="Add any pickup instructions..."
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                    />
                  </Flex>
                  <Flex my={12} flexDirection="row" justifyContent="center">
                    <Button
                      variant="outline"
                      marginRight="10px"
                      isDisabled={isUpdating}
                      w="134px"
                      onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isLoading={isUpdating}
                      isDisabled={!isValid}
                      w="134px">
                      Update
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditCollectionPointModalView;
